import { useEffect, useState } from 'react';

import {
  ArrowButton, Button, ColumnSortEnum, Table, TableMetadata,
  TableStateWithFilters,
  TextField, Typography, useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { CodeBook, ExportTypeEnum } from '@king-ict/eupisi-ui/models';
import { nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { exportEducationSectors } from '../../api/nisooApi';
import { useDidUpdateEffect, useInput } from "../../hooks";
import { CodeBookSearch } from '../../models/CodeBook/CodeBookSearch';
import { useGetEducationSectorsQuery } from '../../queries/codeBookQuery';
import { codebookRouteEducationSector } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookEducationSector(): JSX.Element {
  const storageKey = 'CodeBookEducationSectorSearchTableState';
  const defaultPageSaze = 10;

  const createInitialTableState = (): TableStateWithFilters<CodeBookSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        name: "",
        page: 1,
        pageSize: defaultPageSaze,
        sortBy: 1,
        order: ColumnSortEnum.ASC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<CodeBookSearch>;
    return result;
  }

  const createInitialSearch = (initialTableState: TableStateWithFilters<CodeBookSearch>): CodeBookSearch => {
    return {
      name: initialTableState.name
    }
  }

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [initialTableState] = useState<TableStateWithFilters<CodeBookSearch>>(() => createInitialTableState());
  const [searchFilter, setSearchFilter] = useState<CodeBookSearch>(() => createInitialSearch(initialTableState));
  const [tableEducationSectors, setTableEducationSectors] = useState<CodeBook[]>([]);

  const { data: educationSectors } = useGetEducationSectorsQuery();
  const [itemCount, setItemCount] = useState(educationSectors?.length ?? 0);

  const tableMetadata: TableMetadata<CodeBook> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("codeBook.educationSector"),
        accessor: "name",
        sortBy: 1
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("codeBook.educationSectors.goto")}
              onClick={() => { navigateToDetails(item) }}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  var [tableProps, tableState, setFilters] = useTable<CodeBookSearch>(
    {
      metadata: tableMetadata,
      data: tableEducationSectors,
      pagination: true,
      paginationPage: initialTableState.page as any,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: itemCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  const search = async () => {
    setFilters(searchFilter);
  };

  const clearFilters = async () => {
    const clearSearchFilter = { name: "" }
    setSearchFilter(clearSearchFilter);
    sessionStorage.setItem(storageKey, JSON.stringify({
      name: "",
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
    if (educationSectors) {
      setTableEducationSectors(educationSectors);
      setFilters(clearSearchFilter);
    }
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!educationSectors) {
      return;
    }
    const filteredEducationSectors = () => {
      let filter = tableState.name ? tableState.name?.toLowerCase() : "";

      let filteredEducationSectors = educationSectors
        .filter((item) => item.name.toLowerCase()
          .includes(filter))
        .sort((x, y) => tableState.order === ColumnSortEnum.ASC ? x.name.localeCompare(y.name) : (-1) * x.name.localeCompare(y.name))

      setItemCount(filteredEducationSectors.length);
      let pageSize = (tableProps.paginationProps?.pageSize ?? 0);
      let startIndex = ((tableProps.paginationProps?.page ?? 1) - 1) * pageSize;
      let endIndex = startIndex + pageSize;
      return filteredEducationSectors.slice(startIndex, endIndex);
    }
    setTableEducationSectors(filteredEducationSectors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, educationSectors]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...searchFilter,
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
  }, [tableState])

  const navigateToDetails = (item: CodeBook) => {
    history.push(
      codebookRouteEducationSector(),
      {
        page: "details",
        educationSector: item
      }
    );
  }

  const download = async (exportType: ExportTypeEnum) => {
    exportEducationSectors(exportType);
  }

  return (
    <>
      <Row className="mt-5">
        <Col md={8}>
          <h3>{t("codeBook.educationSector")}</h3>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col md={6}>
          <TextField
            label={t("codeBook.educationSector")}
            name={nameof<typeof searchFilter>("name")}
            value={searchFilter.name}
            maxLength={50}
            onChange={handleChange(setSearchFilter)}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-5"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table  {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv" })}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx" })}
          </Button>
        </Col>
      </Row>
    </>
  );
}

